import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, MenuItem, Select,
  FormControl, InputLabel, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

// Deposits tagged with this marker are internal transfers between the sides of a
// deal, not money arriving from outside. Accounting excludes them from the
// deposits report and the dashboards total them separately.
const TRANSFER_MARKER = 'TFD';

export default function DepositModal({
  open, onClose, stockNumber, deposit, onSaved, type = 'sales',
  defaultAmount = 0, defaultAccount = '',
}) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const isEdit = !!deposit;
  const [form, setForm] = React.useState({ account: '', amount: '', memo: '', stock: '' });
  const [file, setFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      if (deposit) {
        setForm({
          account: deposit.account || '',
          amount: deposit.amount != null ? String(deposit.amount) : '',
          memo: deposit.memo || '',
          stock: deposit.stock || '',
        });
      } else {
        setForm({
          account: defaultAccount || '',
          amount: defaultAmount ? String(defaultAmount) : '',
          memo: '',
          stock: '',
        });
      }
      setFile(null);
    }
  }, [open, deposit, defaultAmount, defaultAccount]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.account || !form.amount || !form.memo) {
      StateManager.setAlertAndOpen('Please fill in all required fields', 'error');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const updates = {
          account: form.account,
          amount: parseFloat(form.amount) || 0,
          memo: form.memo,
        };
        // Admins can re-point a deposit to a different service order (SO/stock number).
        if (StateManager.isAdmin() && form.stock.trim()) {
          updates.stock = form.stock.trim();
        }
        if (file) {
          const location = `receipts/${uuidv4()}`;
          await firebase.storage().ref(location).put(file);
          updates.files = [location];
        }
        await firebase.firestore().collection('deposits').doc(deposit.id).update(updates);
        StateManager.setAlertAndOpen('Deposit updated', 'success');
        if (onSaved) onSaved({ ...deposit, ...updates });
        onClose();
      } else {
        const now = moment().format('YYYY/MM/DD');
        const docRef = firebase.firestore().collection('deposits').doc();
        const data = {
          d: docRef.id,
          stock: stockNumber,
          account: form.account,
          type,
          date: now,
          amount: parseFloat(form.amount) || 0,
          memo: form.memo,
        };
        if (file) {
          const location = `receipts/${uuidv4()}`;
          await firebase.storage().ref(location).put(file);
          data.files = [location];
        }
        await docRef.set(data);
        if (form.account === 'safe') {
          await firebase.firestore().collection('cash').doc().set({
            date: now,
            assigned: false,
            source: 'sales',
            memo: form.memo,
            amount: data.amount,
            user: stockNumber,
          }, { merge: true });
        }
        // On the non-sales sides of a deal (finance, shipping), a TFD-tagged
        // deposit is paid out of the deal itself, so it needs an offsetting paid
        // expense on the same stock number. Mirrors the `car-deposits` form
        // callback in store.js, which is where these pairs came from.
        if (['finance', 'shipping', 'shipping_in'].includes(type) && form.memo.includes(TRANSFER_MARKER)) {
          const purchaseRef = firebase.firestore().collection('purchases').doc();
          await purchaseRef.set({
            e: purchaseRef.id,
            stock: stockNumber,
            date: now,
            paidDate: now,
            vendor: 'Skyway Classics',
            amount: data.amount,
            memo: `${TRANSFER_MARKER} - transfer to ${type}`,
            isPayable: false,
            assigned: false,
          });
        }
        StateManager.setAlertAndOpen('Deposit added', 'success');
        if (onSaved) onSaved({ ...data, id: docRef.id });
        onClose();
      }
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen(isEdit ? 'Failed to update deposit' : 'Failed to add deposit', 'error');
    }
    setSaving(false);
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEdit ? 'Edit Deposit' : 'Add Deposit'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Receiving Bank</InputLabel>
              <Select value={form.account} onChange={handleChange('account')} label="Receiving Bank" MenuProps={selectMenuProps}>
                {StateManager.deposit_banks.map(bank => (
                  <MenuItem key={bank.value} value={bank.value}>{bank.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Amount" type="number"
              value={form.amount} onChange={handleChange('amount')} required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Memo"
              value={form.memo} onChange={handleChange('memo')} required
            />
          </Grid>
          {isEdit && StateManager.isAdmin() && (
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Service Order (SO #)"
                value={form.stock} onChange={handleChange('stock')}
                helperText="Admin only — move this deposit to a different SO"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="outlined" size="small" component="label" startIcon={<AttachFileIcon />}>
              {file ? file.name : 'Attach Receipt (optional)'}
              <input type="file" hidden onChange={e => setFile(e.target.files[0] || null)} />
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
